import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../styles/richTextEditor.css';
import RichTextRgbColorPopover from './RichTextRgbColorPopover';
import RichTextFontSizePopover from './RichTextFontSizePopover';
import {
  EDITOR_FONT_SIZE_PX,
  EDITOR_FONT_WHITELIST,
  clampEditorFontSizePx,
} from '../../utils/editorTypography';
import { parseColorToRgb, rgbPartsToHex } from '../../utils/rgbColor';

const Quill = ReactQuill.Quill;

function quillInlineColorValue(cssColor) {
  const parsed = parseColorToRgb(cssColor, '#000000');
  const alpha = parsed.alpha ?? 1;
  if (alpha < 1) {
    return `rgba(${parsed.r}, ${parsed.g}, ${parsed.b}, ${Math.round(alpha * 1000) / 1000})`;
  }
  return rgbPartsToHex(parsed.r, parsed.g, parsed.b);
}

function applyQuillInlineFormat(quill, savedRange, attr, value) {
  if (!quill) return;
  const range = savedRange || quill.getSelection(true);
  if (!range) return;
  quill.focus();
  quill.setSelection(range.index, range.length, Quill.sources.SILENT);
  if (range.length > 0) {
    quill.formatText(range.index, range.length, attr, value, Quill.sources.USER);
  } else {
    quill.format(attr, value, Quill.sources.USER);
  }
}

const BASE_FONT_WHITELIST = EDITOR_FONT_WHITELIST.filter((f) => f !== 'site-font');

const RICH_TEXT_FORMATS = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'script',
  'list',
  'bullet',
  'indent',
  'align',
  'blockquote',
  'code-block',
  'link',
];

const RICH_TEXT_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    [{ font: BASE_FONT_WHITELIST }],
    [{ size: EDITOR_FONT_SIZE_PX }, 'customFontSize'],
    ['bold', 'italic', 'underline', 'strike'],
    ['customColor', 'customBackground'],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const INLINE_IMAGE_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';
const INLINE_VIDEO_ACCEPT =
  'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov';

if (!Quill.__crmRichTextConfigured) {
  const Font = Quill.import('formats/font');
  Font.whitelist = EDITOR_FONT_WHITELIST;
  Quill.register(Font, true);

  const SizeStyle = Quill.import('attributors/style/size');
  SizeStyle.whitelist = EDITOR_FONT_SIZE_PX;
  Quill.register(SizeStyle, true);

  const ColorStyle = Quill.import('attributors/style/color');
  const BackgroundStyle = Quill.import('attributors/style/background');
  Quill.register(ColorStyle, true);
  Quill.register(BackgroundStyle, true);

  const BlockEmbed = Quill.import('blots/block/embed');

  class VideoBlot extends BlockEmbed {
    static create(value) {
      const node = super.create();
      if (typeof value === 'string' && value.trim()) {
        node.setAttribute('src', value.trim());
      }
      node.setAttribute('controls', 'true');
      node.setAttribute('playsinline', 'true');
      node.setAttribute('preload', 'metadata');
      return node;
    }

    static value(node) {
      return node.getAttribute('src') || '';
    }
  }

  VideoBlot.blotName = 'video';
  VideoBlot.tagName = 'video';
  Quill.register(VideoBlot, true);

  const icons = Quill.import('ui/icons');
  icons.customColor = icons.color;
  icons.customBackground = icons.background;
  icons.customFontSize =
    '<span class="rte-icon-font-size" aria-hidden="true">Aa</span>';

  Quill.__crmRichTextConfigured = true;
}

function toolbarWithFonts(toolbar, fontWhitelist) {
  return toolbar.map((row) => {
    if (Array.isArray(row) && row[0]?.font) {
      return [{ font: fontWhitelist }];
    }
    return row;
  });
}

function toolbarWithOptionalMedia({ enableImages, enableVideos }) {
  if (!enableImages && !enableVideos) return RICH_TEXT_MODULES.toolbar;
  return RICH_TEXT_MODULES.toolbar.map((row) => {
    if (Array.isArray(row) && row.length === 1 && row[0] === 'link') {
      const items = ['link'];
      if (enableImages) items.push('image');
      if (enableVideos) items.push('video');
      return items;
    }
    return row;
  });
}

/**
 * Éditeur HTML riche (Quill) — polices, couleurs, titres, listes, liens.
 * `enableImages` / `enableVideos` + callbacks d’upload pour insérer des médias inline.
 */
export default function RichTextEditor({
  value,
  onChange,
  readOnly = false,
  className = '',
  minEditorHeight = 140,
  placeholder,
  enableImages = false,
  enableVideos = false,
  onImageUpload,
  onVideoUpload,
  rgbColorPicker = true,
  fontWhitelist = BASE_FONT_WHITELIST,
  adaptiveDarkChrome = false,
  defaultEditorFontFamily = null,
}) {
  const containerMinHeight = minEditorHeight + 20;
  const quillRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const [rgbPick, setRgbPick] = useState(null);
  const [sizePick, setSizePick] = useState(null);

  const fontWhitelistKey = fontWhitelist.join('|');
  useMemo(() => {
    const Font = Quill.import('formats/font');
    Font.whitelist = fontWhitelist;
    Quill.register(Font, true);
  }, [fontWhitelistKey, fontWhitelist]);

  const handleChange = useCallback(
    (content, _delta, source) => {
      if (source === 'api') return;
      onChange(content);
    },
    [onChange],
  );

  useEffect(() => {
    let detach = null;
    let cancelled = false;

    const bindToolbarSelection = () => {
      detach?.();
      detach = null;
      const quill = quillRef.current?.getEditor?.();
      const container = quill?.getModule('toolbar')?.container;
      if (!quill || !container) return false;
      const onMouseDown = () => {
        savedSelectionRef.current = quill.getSelection();
      };
      container.addEventListener('mousedown', onMouseDown);
      detach = () => container.removeEventListener('mousedown', onMouseDown);
      return true;
    };

    if (!bindToolbarSelection()) {
      const t = window.setTimeout(() => {
        if (!cancelled) bindToolbarSelection();
      }, 0);
      return () => {
        cancelled = true;
        window.clearTimeout(t);
        detach?.();
      };
    }

    return () => detach?.();
  }, [fontWhitelistKey]);

  const onImageUploadRef = useRef(onImageUpload);
  onImageUploadRef.current = onImageUpload;
  const onVideoUploadRef = useRef(onVideoUpload);
  onVideoUploadRef.current = onVideoUpload;

  const pickAndInsertImage = useCallback(() => {
    const upload = onImageUploadRef.current;
    if (!upload) return;

    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', INLINE_IMAGE_ACCEPT);
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const url = await upload(file);
        if (!url) return;
        const quill = quillRef.current?.getEditor?.();
        if (!quill) return;
        const range = quill.getSelection(true);
        const index = range?.index ?? Math.max(0, quill.getLength() - 1);
        quill.insertEmbed(index, 'image', url);
        quill.setSelection(index + 1);
      } catch {
        /* erreur gérée par le callback appelant (ex. setFormError) */
      }
    };
    input.click();
  }, []);

  const pickAndInsertVideo = useCallback(() => {
    const upload = onVideoUploadRef.current;
    if (!upload) return;

    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', INLINE_VIDEO_ACCEPT);
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const url = await upload(file);
        if (!url) return;
        const quill = quillRef.current?.getEditor?.();
        if (!quill) return;
        const range = quill.getSelection(true);
        const index = range?.index ?? Math.max(0, quill.getLength() - 1);
        quill.insertEmbed(index, 'video', url);
        quill.setSelection(index + 1);
      } catch {
        /* erreur gérée par le callback appelant */
      }
    };
    input.click();
  }, []);

  const openRgbPick = useCallback((formatKey, quill, container) => {
    if (!quill || !container) return;
    const btn = container.querySelector(`button.ql-${formatKey}`);
    const rect = btn?.getBoundingClientRect?.();
    if (!rect) return;
    const savedRange = quill.getSelection(true) || savedSelectionRef.current;
    const attr = formatKey === 'customColor' ? 'color' : 'background';
    const current = savedRange
      ? quill.getFormat(savedRange)?.[attr]
      : quill.getFormat()?.[attr];
    setRgbPick({
      formatKey,
      quill,
      rect,
      savedRange,
      initialValue: typeof current === 'string' ? current : undefined,
    });
  }, []);

  const openSizePick = useCallback((quill, container) => {
    if (!quill || !container) return;
    const btn = container.querySelector('button.ql-customFontSize');
    const rect = btn?.getBoundingClientRect?.();
    if (!rect) return;
    const current = quill.getFormat()?.size;
    let initialPx = 16;
    if (typeof current === 'string' && current.endsWith('px')) {
      initialPx = clampEditorFontSizePx(parseInt(current, 10));
    }
    setSizePick({ quill, rect, initialPx });
  }, []);

  const modules = useMemo(() => {
    let toolbarRows = toolbarWithOptionalMedia({ enableImages, enableVideos });
    toolbarRows = toolbarWithFonts(toolbarRows, fontWhitelist);
    toolbarRows = toolbarRows.map((row) => {
      if (
        !rgbColorPicker &&
        Array.isArray(row) &&
        row[0] === 'customColor' &&
        row[1] === 'customBackground'
      ) {
        return [{ color: [] }, { background: [] }];
      }
      return row;
    });

    const handlers = {};
    if (enableImages) handlers.image = pickAndInsertImage;
    if (enableVideos) handlers.video = pickAndInsertVideo;

    if (rgbColorPicker) {
      handlers.customColor = function handleCustomColor() {
        openRgbPick('customColor', this.quill, this.container);
      };
      handlers.customBackground = function handleCustomBackground() {
        openRgbPick('customBackground', this.quill, this.container);
      };
    }

    handlers.customFontSize = function handleCustomFontSize() {
      openSizePick(this.quill, this.container);
    };

    const useHandlerToolbar = true;

    return {
      ...RICH_TEXT_MODULES,
      toolbar: useHandlerToolbar
        ? { container: toolbarRows, handlers }
        : toolbarRows,
    };
  }, [
    enableImages,
    enableVideos,
    pickAndInsertImage,
    pickAndInsertVideo,
    openRgbPick,
    openSizePick,
    rgbColorPicker,
    fontWhitelist,
  ]);

  const formats = useMemo(() => {
    const extra = [];
    if (enableImages) extra.push('image');
    if (enableVideos) extra.push('video');
    return extra.length ? [...RICH_TEXT_FORMATS, ...extra] : RICH_TEXT_FORMATS;
  }, [enableImages, enableVideos]);

  return (
    <div
      className={`bg-background rounded-md border border-input rich-text-editor-shell${
        adaptiveDarkChrome ? ' rich-text-editor-shell--dark-chrome' : ''
      }`}
      style={{
        '--rte-editor-min-h': `${minEditorHeight}px`,
        '--rte-container-min-h': `${containerMinHeight}px`,
        ...(defaultEditorFontFamily
          ? { '--rte-default-font-family': defaultEditorFontFamily }
          : {}),
      }}
    >
      <div className="[&_.ql-container]:min-h-[var(--rte-container-min-h)] [&_.ql-editor]:min-h-[var(--rte-editor-min-h)]">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleChange}
          readOnly={readOnly}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          className={`react-quill-editor ${className}`.trim()}
        />
      </div>
      {rgbColorPicker && rgbPick ? (
        <RichTextRgbColorPopover
          open
          anchorRect={rgbPick.rect}
          title={
            rgbPick.formatKey === 'customColor'
              ? 'Couleur du texte (RGB)'
              : 'Couleur de surbrillance (RGB)'
          }
          initialValue={rgbPick.initialValue}
          onApply={(cssColor) => {
            const attr = rgbPick.formatKey === 'customColor' ? 'color' : 'background';
            applyQuillInlineFormat(
              rgbPick.quill,
              rgbPick.savedRange,
              attr,
              quillInlineColorValue(cssColor),
            );
          }}
          onClear={() => {
            const attr = rgbPick.formatKey === 'customColor' ? 'color' : 'background';
            applyQuillInlineFormat(rgbPick.quill, rgbPick.savedRange, attr, false);
          }}
          onClose={() => setRgbPick(null)}
        />
      ) : null}
      {sizePick ? (
        <RichTextFontSizePopover
          open
          anchorRect={sizePick.rect}
          initialPx={sizePick.initialPx}
          onApply={(sizeValue) => {
            if (sizeValue === false) {
              sizePick.quill.format('size', false);
            } else {
              sizePick.quill.format('size', sizeValue);
            }
          }}
          onClose={() => setSizePick(null)}
        />
      ) : null}
    </div>
  );
}
